import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  PackageSearch, 
  Search, 
  Filter, 
  SlidersHorizontal,
  Download, 
  Printer, 
  Plus, 
  ArrowRightLeft, 
  ChevronLeft, 
  ChevronRight, 
  Warehouse, 
  Users, 
  ShieldCheck, 
  AlertTriangle, 
  TrendingDown, 
  DollarSign, 
  Layers, 
  RefreshCw, 
  CheckCircle2, 
  X,
  History,
  Building2,
  ExternalLink,
  Tag,
  Calendar,
  IndianRupee,
  HelpCircle,
  FolderTree,
  AlertOctagon,
  Clock,
  RotateCcw,
  CheckCircle,
  Archive,
  Wrench,
  Boxes
} from "lucide-react";
import toast from "react-hot-toast";
import ManageStockModal from "./ManageStockModal";
import StockLedgerModal from "./StockLedgerModal";

const API = process.env.REACT_APP_API_URL;

// ─── 9 SPECIFICATION FIELDS REFERENCE DATA (From Spec Image) ─────────────────
const SPEC_CRITERIA = [
  {
    field: "Product / SKU",
    options: "Product name, SKU, barcode, internal code",
    purpose: "Locate exact inventory record.",
  },
  {
    field: "Owner Type",
    options: "Admin / Vendor",
    purpose: "Separate ownership and settlement responsibility.",
  },
  {
    field: "Vendor",
    options: "Vendor/store selector",
    purpose: "Vendor-specific stock visibility.",
  },
  {
    field: "Warehouse",
    options: "Warehouse / branch / fulfilment location",
    purpose: "Location-based stock control.",
  },
  {
    field: "Category",
    options: "Multi-level category",
    purpose: "Category inventory analysis.",
  },
  {
    field: "Stock Status",
    options: "Good, Reserved, In Transit, QC Hold, Returned, Damaged, Repair, Rejected, Lost/Missing",
    purpose: "Operational stock condition.",
  },
  {
    field: "Stock Level",
    options: "All / Low / Out / Negative exception",
    purpose: "Inventory alert management.",
  },
  {
    field: "Date Range",
    options: "Created / received / movement date",
    purpose: "Historical investigation.",
  },
  {
    field: "Value Range",
    options: "Cost / valuation range",
    purpose: "Inventory financial analysis.",
  },
];

const AllInventory = ({ scope: propScope }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current active scope from prop or pathname
  const currentScope = useMemo(() => {
    if (propScope) return propScope;
    if (location.pathname.includes("/admin-stock")) return "admin";
    if (location.pathname.includes("/vendor-stock")) return "vendor";
    return "all";
  }, [propScope, location.pathname]);

  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dynamic filter options from server
  const [filterOptions, setFilterOptions] = useState({
    warehouses: [],
    vendors: [],
    categories: [],
    subCategories: [],
  });

  // ─── THE 9 SPECIFICATION FILTERS STATE ────────────────────────────────────
  // 1. Product / SKU / Barcode / Code search
  const [searchTerm, setSearchTerm] = useState("");

  // 2. Owner Type ("all" | "admin" | "vendor")
  const [selectedOwnerType, setSelectedOwnerType] = useState(currentScope !== "all" ? currentScope : "all");

  // 3. Vendor / Store Selector
  const [selectedVendor, setSelectedVendor] = useState("All");

  // 4. Warehouse Location
  const [selectedWarehouse, setSelectedWarehouse] = useState("All");

  // 5. Multi-Level Category: Category + SubCategory
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubCategory, setSelectedSubCategory] = useState("All");

  // 6. Stock Status (Operational condition)
  const [selectedStockStatus, setSelectedStockStatus] = useState("All");

  // 7. Stock Level ("all" | "in" | "low" | "out" | "negative")
  const [selectedStockLevel, setSelectedStockLevel] = useState("all");

  // 8. Date Range
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // 9. Value Range (Cost / valuation)
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  // Sort & Pagination
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // UI Panels & Modals
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showSpecModal, setShowSpecModal] = useState(false);
  const [quickFilterOpen, setQuickFilterOpen] = useState(false);

  // Stock Management Modal State
  const [activeProduct, setActiveProduct] = useState(null);
  const [manageModalTab, setManageModalTab] = useState("add");
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false);

  // Sync owner type when currentScope changes
  useEffect(() => {
    if (currentScope !== "all") {
      setSelectedOwnerType(currentScope);
    }
  }, [currentScope]);

  // Fetch filter options (warehouses, vendors, categories, subcategories)
  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/inventory/filter-options`);
      if (res.data.success) {
        setFilterOptions({
          warehouses: res.data.warehouses || [],
          vendors: res.data.vendors || [],
          categories: res.data.categories || [],
          subCategories: res.data.subCategories || [],
        });
      }
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };

  // Filtered subcategories based on chosen category
  const availableSubCategories = useMemo(() => {
    if (!selectedCategory || selectedCategory === "All") {
      return filterOptions.subCategories || [];
    }
    return (filterOptions.subCategories || []).filter(
      (sc) => String(sc.categoryId) === String(selectedCategory)
    );
  }, [selectedCategory, filterOptions.subCategories]);

  // Fetch products dynamically based on all 9 criteria
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveScope = selectedOwnerType !== "all" ? selectedOwnerType : currentScope;

      const params = {
        scope: effectiveScope,
        ownerType: selectedOwnerType !== "all" ? selectedOwnerType : undefined,
        warehouse: selectedWarehouse !== "All" ? selectedWarehouse : undefined,
        vendorId: selectedVendor !== "All" ? selectedVendor : undefined,
        category: selectedCategory !== "All" ? selectedCategory : undefined,
        subCategory: selectedSubCategory !== "All" ? selectedSubCategory : undefined,
        stockStatus: selectedStockStatus !== "All" ? selectedStockStatus : undefined,
        stockLevel: selectedStockLevel !== "all" ? selectedStockLevel : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        minValue: minValue !== "" ? minValue : undefined,
        maxValue: maxValue !== "" ? maxValue : undefined,
        search: searchTerm.trim() || undefined,
        sortBy,
        sortOrder,
        page,
        limit,
      };

      const res = await axios.get(`${API}/inventory/items`, { params });
      if (res.data.success) {
        setProducts(res.data.products || []);
        setStats(res.data.stats || null);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error("Failed to fetch inventory items:", err);
      toast.error("Failed to load inventory data");
    } finally {
      setLoading(false);
    }
  }, [
    currentScope,
    selectedOwnerType,
    selectedWarehouse,
    selectedVendor,
    selectedCategory,
    selectedSubCategory,
    selectedStockStatus,
    selectedStockLevel,
    dateFrom,
    dateTo,
    minValue,
    maxValue,
    searchTerm,
    sortBy,
    sortOrder,
    page,
    limit,
  ]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset page when scope changes
  useEffect(() => {
    setPage(1);
  }, [currentScope]);

  // Active filter count calculator
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm.trim()) count++;
    if (selectedOwnerType !== "all" && selectedOwnerType !== currentScope) count++;
    if (selectedWarehouse !== "All") count++;
    if (selectedVendor !== "All") count++;
    if (selectedCategory !== "All") count++;
    if (selectedSubCategory !== "All") count++;
    if (selectedStockStatus !== "All") count++;
    if (selectedStockLevel !== "all") count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    if (minValue !== "") count++;
    if (maxValue !== "") count++;
    return count;
  }, [
    searchTerm,
    selectedOwnerType,
    currentScope,
    selectedWarehouse,
    selectedVendor,
    selectedCategory,
    selectedSubCategory,
    selectedStockStatus,
    selectedStockLevel,
    dateFrom,
    dateTo,
    minValue,
    maxValue,
  ]);

  // Reset all 9 filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedOwnerType(currentScope !== "all" ? currentScope : "all");
    setSelectedWarehouse("All");
    setSelectedVendor("All");
    setSelectedCategory("All");
    setSelectedSubCategory("All");
    setSelectedStockStatus("All");
    setSelectedStockLevel("all");
    setDateFrom("");
    setDateTo("");
    setMinValue("");
    setMaxValue("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setPage(1);
    toast.success("All filters reset");
  };

  // Top action: Open Add Stock Modal
  const handleOpenAddStock = () => {
    setActiveProduct(products[0] || null);
    setManageModalTab("add");
    setIsManageModalOpen(true);
  };

  // Top action: Open Transfer Modal
  const handleOpenTransfer = () => {
    setActiveProduct(products[0] || null);
    setManageModalTab("transfer");
    setIsManageModalOpen(true);
  };

  // Top action: Open Adjustment Modal
  const handleOpenAdjustment = () => {
    setActiveProduct(products[0] || null);
    setManageModalTab("adjust");
    setIsManageModalOpen(true);
  };

  // Export CSV
  const handleExportCSV = () => {
    if (products.length === 0) {
      toast.error("No products to export");
      return;
    }

    const headers = [
      "Product Title",
      "SKU",
      "BHKG ID",
      "Owner Type",
      "Vendor / Artisan",
      "Warehouse Location",
      "Storage Rack/Bin",
      "Available Stock",
      "Stock Status Condition",
      "Unit Price (INR)",
      "Stock Valuation (INR)",
      "Category",
      "Subcategory",
      "Created Date",
    ];

    const rows = products.map((p) => [
      `"${(p.name?.en || p.name || "").replace(/"/g, '""')}"`,
      `"${p.sku || ""}"`,
      `"${p.bhkg_id || ""}"`,
      p.uploadedBy === "admin" ? "Admin" : "Vendor",
      `"${p.supplier?.businessName || p.supplier?.fullName || "--"}"`,
      `"${p.warehouse || "Bhubaneswar (Main)"}"`,
      `"${p.rackBinLocation || "A-01-01"}"`,
      p.stock || 0,
      p.stock_status || "Good",
      p.price || 0,
      (p.stock || 0) * (p.price || 0),
      `"${typeof p.category?.name === "object" ? p.category.name.en : p.category?.name || ""}"`,
      `"${typeof p.subCategory?.name === "object" ? p.subCategory.name.en : p.subCategory?.name || ""}"`,
      p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-IN") : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `all_inventory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV export downloaded successfully!");
  };

  // Print Stock List
  const handlePrint = () => {
    window.print();
  };

  // Stock status pill helper
  const getStatusBadge = (product) => {
    const rawStatus = (product.stock_status || "active").toLowerCase();
    const stockQty = Number(product.stock) || 0;

    if (stockQty <= 0 || rawStatus === "out of stock") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-rose-100 text-rose-800 border border-rose-200">
          <AlertOctagon size={11} /> Out of Stock
        </span>
      );
    }
    if (rawStatus === "qc hold" || product.approval_status === "QC Hold") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200">
          <Clock size={11} /> QC Hold
        </span>
      );
    }
    if (rawStatus === "damaged") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-red-100 text-red-800 border border-red-200">
          <AlertTriangle size={11} /> Damaged
        </span>
      );
    }
    if (rawStatus === "repair") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-indigo-100 text-indigo-800 border border-indigo-200">
          <Wrench size={11} /> Repair
        </span>
      );
    }
    if (rawStatus === "returned") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-purple-100 text-purple-800 border border-purple-200">
          <RotateCcw size={11} /> Returned
        </span>
      );
    }
    if (rawStatus === "active" || rawStatus === "good") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-200">
          <CheckCircle2 size={11} /> Good (In Stock)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-gray-100 text-gray-700 border border-gray-300">
        {product.stock_status || "Active"}
      </span>
    );
  };

  return (
    <section className="min-h-screen bg-[#f3f4f6] p-4 sm:p-6 font-sans">
      {/* ── TOP HEADER MATCHING SPECIFICATION TITLE ── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>Admin</span>
            <span>›</span>
            <span>Inventory</span>
            <span>›</span>
            <span className="text-[#b07d17] font-semibold">All Inventory Screen</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#131921] to-[#232f3e] flex items-center justify-center text-[#febd69] shadow-sm">
              <PackageSearch className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight font-sans">
                  3. ALL INVENTORY — ADMIN SCREEN
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  Master Stock Register
                </span>
                <button
                  onClick={() => setShowSpecModal(true)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition"
                  title="View Specification Table"
                >
                  <HelpCircle size={12} /> View Screen Spec
                </button>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Centralized multi-level stock control, warehouse distribution, and operational condition analytics.
              </p>
            </div>
          </div>
        </div>

        {/* Global Quick Status Badges */}
        <div className="flex items-center gap-2 self-start lg:self-center">
          <button
            onClick={() => fetchProducts()}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-700 shadow-sm transition"
            title="Refresh Data"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-[#b07d17]" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* ── AMAZON-STYLE SCOPE TABS ── */}
      <div className="flex items-center gap-2 p-1.5 bg-white border border-gray-200 rounded-xl shadow-sm mb-4 overflow-x-auto">
        <button
          onClick={() => {
            setSelectedOwnerType("all");
            navigate("/admin-inventory/all-inventory");
          }}
          className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition ${
            currentScope === "all" && selectedOwnerType === "all"
              ? "bg-[#131921] text-[#febd69] shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          }`}
        >
          <PackageSearch size={15} /> All Inventory (All Stock)
        </button>
        <button
          onClick={() => {
            setSelectedOwnerType("admin");
            navigate("/admin-inventory/admin-stock");
          }}
          className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition ${
            currentScope === "admin" || selectedOwnerType === "admin"
              ? "bg-[#131921] text-[#febd69] shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          }`}
        >
          <ShieldCheck size={15} /> Admin Stock
        </button>
        <button
          onClick={() => {
            setSelectedOwnerType("vendor");
            navigate("/admin-inventory/vendor-stock");
          }}
          className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition ${
            currentScope === "vendor" || selectedOwnerType === "vendor"
              ? "bg-[#131921] text-[#febd69] shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          }`}
        >
          <Users size={15} /> Vendor Stock
        </button>
        <button
          onClick={() => navigate("/admin-inventory/warehouse")}
          className="px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition"
        >
          <Warehouse size={15} /> Warehouse Hub
        </button>
        <button
          onClick={() => navigate("/admin-inventory/stock-movement")}
          className="px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition"
        >
          <Boxes size={15} /> Stock Movement
        </button>
      </div>

      {/* ── TOP CONTROLS TOOLBAR (EXACTLY MATCHING SPECIFICATION IMAGE) ── */}
      {/* Top controls: Search SKU/Product | Filter | Advanced Filter | Export | Print | Add Stock | Transfer | Adjustment | Stock Count */}
      <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-gray-200 shadow-sm mb-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          {/* 1. Search SKU/Product Input */}
          <div className="relative flex-1 min-w-[260px]">
            <input
              type="text"
              placeholder="Search SKU / Product / Barcode / Internal Code..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17] transition bg-[#fdfdfd]"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X size={15} />
              </button>
            )}
          </div>

          {/* Top Controls Action Buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Control 2: Quick Filter Toggle */}
            <div className="relative">
              <button
                onClick={() => setQuickFilterOpen(!quickFilterOpen)}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                  selectedStockLevel !== "all"
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                title="Quick Stock Level Filter"
              >
                <Filter size={13} />
                <span>Filter</span>
                {selectedStockLevel !== "all" && (
                  <span className="w-2 h-2 rounded-full bg-amber-500 ml-0.5"></span>
                )}
              </button>

              {/* Quick Filter Dropdown */}
              {quickFilterOpen && (
                <div className="absolute left-0 sm:right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 text-xs">
                  <div className="px-3 py-1 font-bold text-gray-500 uppercase text-[10px] border-b">
                    Stock Level Filter
                  </div>
                  <button
                    onClick={() => { setSelectedStockLevel("all"); setQuickFilterOpen(false); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${selectedStockLevel === "all" ? "font-bold text-[#b07d17]" : "text-gray-700"}`}
                  >
                    All Levels
                  </button>
                  <button
                    onClick={() => { setSelectedStockLevel("in"); setQuickFilterOpen(false); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${selectedStockLevel === "in" ? "font-bold text-emerald-700" : "text-gray-700"}`}
                  >
                    In Stock (&gt;0)
                  </button>
                  <button
                    onClick={() => { setSelectedStockLevel("low"); setQuickFilterOpen(false); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${selectedStockLevel === "low" ? "font-bold text-amber-700" : "text-gray-700"}`}
                  >
                    Low Stock (1-20 units)
                  </button>
                  <button
                    onClick={() => { setSelectedStockLevel("out"); setQuickFilterOpen(false); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${selectedStockLevel === "out" ? "font-bold text-rose-700" : "text-gray-700"}`}
                  >
                    Out of Stock (0 units)
                  </button>
                  <button
                    onClick={() => { setSelectedStockLevel("negative"); setQuickFilterOpen(false); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${selectedStockLevel === "negative" ? "font-bold text-purple-700" : "text-gray-700"}`}
                  >
                    Negative Exception (&lt;0)
                  </button>
                </div>
              )}
            </div>

            {/* Control 3: Advanced Filter Button */}
            <button
              onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                showAdvancedFilter || activeFilterCount > 0
                  ? "bg-[#b07d17] text-white border-[#b07d17] shadow-sm"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              <SlidersHorizontal size={13} />
              <span>Advanced Filter</span>
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-white text-[#b07d17] text-[10px] font-black flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Control 4: Export Button */}
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-700 shadow-sm transition"
              title="Export Inventory CSV"
            >
              <Download size={13} />
              <span>Export</span>
            </button>

            {/* Control 5: Print Button */}
            <button
              onClick={handlePrint}
              className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-xs font-semibold text-gray-700 shadow-sm transition"
              title="Print Stock Sheets"
            >
              <Printer size={13} />
              <span>Print</span>
            </button>

            {/* Control 6: Add Stock Button */}
            <button
              onClick={handleOpenAddStock}
              className="flex items-center gap-1 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-semibold shadow-sm transition"
              title="Add New Stock to Inventory"
            >
              <Plus size={13} />
              <span>Add Stock</span>
            </button>

            {/* Control 7: Transfer Button */}
            <button
              onClick={handleOpenTransfer}
              className="flex items-center gap-1 px-3 py-2 bg-[#232f3e] hover:bg-[#131921] text-white rounded-lg text-xs font-semibold shadow-sm transition"
              title="Inter-Warehouse Stock Transfer"
            >
              <ArrowRightLeft size={13} />
              <span>Transfer</span>
            </button>

            {/* Control 8: Adjustment Button */}
            <button
              onClick={handleOpenAdjustment}
              className="flex items-center gap-1 px-3 py-2 bg-[#007185] hover:bg-[#005a6a] text-white rounded-lg text-xs font-semibold shadow-sm transition"
              title="Inventory Stock Adjustment"
            >
              <SlidersHorizontal size={13} />
              <span>Adjustment</span>
            </button>

            {/* Control 9: Stock Count Button */}
            <button
              onClick={() => navigate("/admin-inventory/stock-count")}
              className="flex items-center gap-1 px-3 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-xs font-semibold shadow-sm transition"
              title="Physical Stock Count & Audit"
            >
              <CheckCircle size={13} />
              <span>Stock Count</span>
            </button>
          </div>
        </div>

        {/* Active Filter Chips Strip */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-3 border-t border-gray-100 mt-3 text-xs">
            <span className="text-gray-500 font-medium text-[11px]">Active Filters:</span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px]">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm("")} className="hover:text-blue-900"><X size={11} /></button>
              </span>
            )}

            {selectedOwnerType !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-[11px]">
                Owner: {selectedOwnerType === "admin" ? "Admin" : "Vendor"}
                <button onClick={() => setSelectedOwnerType("all")} className="hover:text-purple-900"><X size={11} /></button>
              </span>
            )}

            {selectedWarehouse !== "All" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px]">
                Warehouse: {selectedWarehouse}
                <button onClick={() => setSelectedWarehouse("All")} className="hover:text-amber-900"><X size={11} /></button>
              </span>
            )}

            {selectedCategory !== "All" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px]">
                Category: {filterOptions.categories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                <button onClick={() => { setSelectedCategory("All"); setSelectedSubCategory("All"); }} className="hover:text-emerald-900"><X size={11} /></button>
              </span>
            )}

            {selectedSubCategory !== "All" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px]">
                SubCategory: {filterOptions.subCategories.find(s => s.id === selectedSubCategory)?.name || selectedSubCategory}
                <button onClick={() => setSelectedSubCategory("All")} className="hover:text-emerald-900"><X size={11} /></button>
              </span>
            )}

            {selectedStockStatus !== "All" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px]">
                Status: {selectedStockStatus}
                <button onClick={() => setSelectedStockStatus("All")} className="hover:text-indigo-900"><X size={11} /></button>
              </span>
            )}

            {selectedStockLevel !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-800 border border-orange-200 text-[11px]">
                Level: {selectedStockLevel}
                <button onClick={() => setSelectedStockLevel("all")} className="hover:text-orange-900"><X size={11} /></button>
              </span>
            )}

            {(dateFrom || dateTo) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-800 border border-teal-200 text-[11px]">
                Date: {dateFrom || "Start"} to {dateTo || "Now"}
                <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="hover:text-teal-900"><X size={11} /></button>
              </span>
            )}

            {(minValue !== "" || maxValue !== "") && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-800 border border-green-200 text-[11px]">
                Value: ₹{minValue || 0} - ₹{maxValue || "∞"}
                <button onClick={() => { setMinValue(""); setMaxValue(""); }} className="hover:text-green-900"><X size={11} /></button>
              </span>
            )}

            <button
              onClick={handleResetFilters}
              className="text-[#b07d17] hover:underline font-semibold ml-auto text-[11px]"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* ── ADVANCED FILTER DRAWER / PANEL (CONTAINING THE 9 FIELDS FROM SPEC) ── */}
      {showAdvancedFilter && (
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-md mb-5 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-4">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#b07d17]" />
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Advanced Inventory Filter Matrix (9 Dimensions)
                </h3>
                <p className="text-xs text-gray-500">
                  Precision targeting across ownership, warehouse, multi-level category, condition, and valuation.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAdvancedFilter(false)}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            {/* Field 1: Product / SKU */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                1. Product / SKU
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Product name, SKU, barcode, internal code
              </span>
              <input
                type="text"
                placeholder="SKU, Barcode or Title..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              />
            </div>

            {/* Field 2: Owner Type */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                2. Owner Type
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Admin / Vendor (Ownership & settlement)
              </span>
              <select
                value={selectedOwnerType}
                onChange={(e) => { setSelectedOwnerType(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              >
                <option value="all">All Ownership Types</option>
                <option value="admin">Admin Stock Only</option>
                <option value="vendor">Vendor / Artisan Stock Only</option>
              </select>
            </div>

            {/* Field 3: Vendor */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                3. Vendor
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Vendor / store selector
              </span>
              <select
                value={selectedVendor}
                onChange={(e) => { setSelectedVendor(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              >
                <option value="All">All Registered Vendors</option>
                {filterOptions.vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.fullName && v.fullName !== v.name ? `(${v.fullName})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Field 4: Warehouse */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                4. Warehouse
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Warehouse / branch / fulfilment location
              </span>
              <select
                value={selectedWarehouse}
                onChange={(e) => { setSelectedWarehouse(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              >
                <option value="All">All Fulfilment Locations</option>
                {filterOptions.warehouses.map((wh) => (
                  <option key={wh.id} value={wh.name}>
                    {wh.name} ({wh.warehouseId})
                  </option>
                ))}
              </select>
            </div>

            {/* Field 5: Category (Multi-level) */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                5. Category (Multi-level)
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Parent Category & Subcategory analysis
              </span>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedSubCategory("All");
                    setPage(1);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                >
                  <option value="All">All Categories</option>
                  {filterOptions.categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <select
                  value={selectedSubCategory}
                  onChange={(e) => { setSelectedSubCategory(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                >
                  <option value="All">All Subcategories</option>
                  {availableSubCategories.map((sc) => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Field 6: Stock Status */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                6. Stock Status
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Operational stock condition
              </span>
              <select
                value={selectedStockStatus}
                onChange={(e) => { setSelectedStockStatus(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              >
                <option value="All">All Conditions (Good & Exceptions)</option>
                <option value="Good">Good (Fulfillable / Active)</option>
                <option value="Reserved">Reserved (Customer Orders)</option>
                <option value="In Transit">In Transit (Inter-warehouse)</option>
                <option value="QC Hold">QC Hold (Quality Inspection)</option>
                <option value="Returned">Returned (Customer RMA)</option>
                <option value="Damaged">Damaged (Unsellable)</option>
                <option value="Repair">Repair (Artisan Restoration)</option>
                <option value="Rejected">Rejected (Correction / QA Fail)</option>
                <option value="Lost/Missing">Lost / Missing (Discrepancy)</option>
              </select>
            </div>

            {/* Field 7: Stock Level */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                7. Stock Level
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Inventory alert management
              </span>
              <select
                value={selectedStockLevel}
                onChange={(e) => { setSelectedStockLevel(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
              >
                <option value="all">All (Complete Stock Spectrum)</option>
                <option value="in">In Stock (&gt;0 Units)</option>
                <option value="low">Low Stock (≤ 20 Units Alert)</option>
                <option value="out">Out of Stock (0 Units Alert)</option>
                <option value="negative">Negative Exception (&lt;0 Units)</option>
              </select>
            </div>

            {/* Field 8: Date Range */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                8. Date Range
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Created / received / movement date
              </span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                />
              </div>
            </div>

            {/* Field 9: Value Range */}
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <label className="block font-bold text-gray-800 mb-0.5">
                9. Value Range
              </label>
              <span className="block text-[11px] text-gray-500 mb-1.5">
                Cost / valuation range (₹)
              </span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Min ₹"
                  value={minValue}
                  onChange={(e) => { setMinValue(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                />
                <input
                  type="number"
                  placeholder="Max ₹"
                  value={maxValue}
                  onChange={(e) => { setMaxValue(e.target.value); setPage(1); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-xs focus:ring-1 focus:ring-[#b07d17]"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
            <span className="text-xs text-gray-500">
              Showing filtered results instantly based on selection.
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetFilters}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-xs font-semibold text-gray-700 transition"
              >
                Reset All 9 Filters
              </button>
              <button
                onClick={() => { setShowAdvancedFilter(false); fetchProducts(); }}
                className="px-4 py-1.5 rounded-lg bg-[#b07d17] hover:bg-[#8c6312] text-xs font-semibold text-white shadow-sm transition"
              >
                Apply & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── KPI TILES ROW ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-5">
        {/* Total SKUs */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Total SKUs</span>
            <Layers className="w-4 h-4 text-[#007185]" />
          </div>
          <div className="text-2xl font-black text-gray-900">
            {stats?.totalProducts ?? pagination.total ?? 0}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Matching filter criteria
          </div>
        </div>

        {/* Available Stock */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Available Stock</span>
            <Building2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-emerald-700">
            {stats?.totalUnits ?? 0} <span className="text-xs font-medium text-gray-500">units</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Admin: {stats?.adminStockUnits ?? 0} · Vendor: {stats?.vendorStockUnits ?? 0}
          </div>
        </div>

        {/* Total Valuation */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Stock Valuation</span>
            <DollarSign className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-gray-900">
            ₹{(stats?.totalValuation ?? 0).toLocaleString("en-IN")}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Asset value at current retail
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Low Stock (≤20)</span>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-2xl font-black text-amber-600">
            {stats?.lowStockCount ?? 0} <span className="text-xs font-medium text-gray-500">items</span>
          </div>
          <div className="text-[11px] text-amber-700 font-medium mt-1">
            Replenishment reorder queue
          </div>
        </div>

        {/* Out of Stock */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Out of Stock</span>
            <TrendingDown className="w-4 h-4 text-rose-500" />
          </div>
          <div className="text-2xl font-black text-rose-600">
            {stats?.outOfStockCount ?? 0} <span className="text-xs font-medium text-gray-500">items</span>
          </div>
          <div className="text-[11px] text-rose-700 font-medium mt-1">
            0 fulfillable units
          </div>
        </div>
      </div>

      {/* ── MASTER INVENTORY TABLE ── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead className="bg-[#f8f9fa] border-b border-gray-200 text-gray-700 font-bold">
              <tr>
                <th className="px-3.5 py-3.5 text-center w-12">#</th>
                <th className="px-3.5 py-3.5">Image</th>
                <th className="px-3.5 py-3.5">Product Title & Multi-level Identifiers</th>
                <th className="px-3.5 py-3.5">Owner Type</th>
                <th className="px-3.5 py-3.5">Warehouse & Bin</th>
                <th className="px-3.5 py-3.5 text-right">Available Qty</th>
                <th className="px-3.5 py-3.5 text-right">Unit Price / Value</th>
                <th className="px-3.5 py-3.5 text-center">Stock Condition</th>
                <th className="px-3.5 py-3.5 text-center">Admin Controls</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-20 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#b07d17] mx-auto mb-2" />
                    <span className="font-semibold text-sm">Querying real-time inventory records...</span>
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((p, idx) => {
                  const prodName = p.name?.en || (typeof p.name === "string" ? p.name : "--");
                  const img = p.images?.[0];
                  const stockQty = Number(p.stock) || 0;
                  const price = Number(p.price) || 0;
                  const assetValue = stockQty * price;
                  const categoryName = typeof p.category?.name === "object" ? p.category.name.en : p.category?.name;
                  const subCatName = typeof p.subCategory?.name === "object" ? p.subCategory.name.en : p.subCategory?.name;

                  return (
                    <tr key={p._id} className="hover:bg-gray-50/90 transition group">
                      {/* Row Index */}
                      <td className="px-3.5 py-3 text-center text-gray-400 font-mono text-xs">
                        {(page - 1) * limit + idx + 1}
                      </td>

                      {/* Image Thumbnail */}
                      <td className="px-3.5 py-3">
                        <div className="w-12 h-12 rounded border border-gray-200 bg-white p-1 flex items-center justify-center shrink-0">
                          {img ? (
                            <img src={img} alt="thumb" className="max-w-full max-h-full object-contain" />
                          ) : (
                            <span className="text-[10px] text-gray-400">No Img</span>
                          )}
                        </div>
                      </td>

                      {/* Product Title & Identifiers */}
                      <td className="px-3.5 py-3">
                        <a
                          href={`/product/${p._id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-[#007185] hover:text-[#c7511f] hover:underline line-clamp-1 max-w-[280px]"
                          title={prodName}
                        >
                          {prodName}
                        </a>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5 flex-wrap">
                          <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[11px] font-semibold text-gray-700">
                            SKU: {p.sku || "--"}
                          </span>
                          <span>•</span>
                          <span className="font-mono text-gray-600 font-medium text-[11px]">
                            ID: {p.bhkg_id || "--"}
                          </span>
                          {categoryName && (
                            <>
                              <span>•</span>
                              <span className="text-gray-600 truncate max-w-[130px] font-medium text-[11px]">
                                {categoryName} {subCatName ? `› ${subCatName}` : ""}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Owner Type */}
                      <td className="px-3.5 py-3">
                        {p.uploadedBy === "admin" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                            <ShieldCheck size={12} /> Admin
                          </span>
                        ) : (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                              <Users size={12} /> {p.supplier?.businessName || p.supplier?.fullName || "Vendor"}
                            </span>
                            {p.supplier?.mobNo && (
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                Mob: {p.supplier.mobNo}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Warehouse & Storage Location */}
                      <td className="px-3.5 py-3">
                        <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                          <Warehouse size={12} className="text-gray-400 shrink-0" />
                          <span className="truncate max-w-[150px]">{p.warehouse || "Bhubaneswar (Main)"}</span>
                        </div>
                        <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                          Bin: {p.rackBinLocation || "A-01-01"}
                        </div>
                      </td>

                      {/* Available Qty */}
                      <td className="px-3.5 py-3 text-right">
                        <div className={`text-base font-extrabold ${stockQty > 20 ? "text-emerald-700" : stockQty > 0 ? "text-amber-700" : "text-rose-700"}`}>
                          {stockQty}
                        </div>
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full ml-auto mt-1 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              stockQty > 20 ? "bg-emerald-500" : stockQty > 0 ? "bg-amber-500" : "bg-rose-500"
                            }`}
                            style={{ width: `${Math.min(100, (stockQty / 50) * 100)}%` }}
                          />
                        </div>
                      </td>

                      {/* Pricing / Valuation */}
                      <td className="px-3.5 py-3 text-right">
                        <div className="font-semibold text-gray-900">
                          ₹{price.toLocaleString("en-IN")}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Val: ₹{assetValue.toLocaleString("en-IN")}
                        </div>
                      </td>

                      {/* Stock Condition */}
                      <td className="px-3.5 py-3 text-center">
                        {getStatusBadge(p)}
                      </td>

                      {/* Admin Controls */}
                      <td className="px-3.5 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => {
                              setActiveProduct(p);
                              setManageModalTab("add");
                              setIsManageModalOpen(true);
                            }}
                            className="bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm transition flex items-center gap-1"
                            title="Manage Stock (Add, Adjust, Transfer)"
                          >
                            <SlidersHorizontal size={12} /> Manage
                          </button>
                          <button
                            onClick={() => {
                              setActiveProduct(p);
                              setIsLedgerModalOpen(true);
                            }}
                            className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-lg px-2 py-1 text-xs font-medium transition"
                            title="Stock Audit Ledger History"
                          >
                            <History size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-16 text-center text-gray-500">
                    <PackageSearch className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-base font-semibold text-gray-700">No inventory products found</p>
                    <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                      No records match the current filter configuration. Try adjusting or resetting your filter criteria.
                    </p>
                    <button
                      onClick={handleResetFilters}
                      className="mt-3 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition"
                    >
                      Reset All 9 Filters
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── PAGINATION CONTROLS ── */}
        {!loading && products.length > 0 && (
          <div className="px-5 py-3.5 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white text-xs">
            <div className="flex items-center gap-2 text-gray-500">
              <span>
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} entries
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                Per page:
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white focus:outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={15} />
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), Math.min(pagination.totalPages, page + 2))
                .map((pNum) => (
                  <button
                    key={pNum}
                    onClick={() => setPage(pNum)}
                    className={`px-2.5 py-1 rounded-lg font-medium transition ${
                      page === pNum
                        ? "bg-[#b07d17] text-white shadow-sm font-bold"
                        : "border border-gray-300 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    {pNum}
                  </button>
                ))}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL 1: SPECIFICATION GUIDE MODAL (EXACT TABLE FROM IMAGE) ── */}
      {showSpecModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-[#131921] px-5 py-4 text-white flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#febd69]" />
                  3. ALL INVENTORY — ADMIN SCREEN SPECIFICATION
                </h3>
                <p className="text-xs text-gray-300 mt-0.5">
                  Top controls: Search SKU/Product | Filter | Advanced Filter | Export | Print | Add Stock | Transfer | Adjustment | Stock Count
                </p>
              </div>
              <button
                onClick={() => setShowSpecModal(false)}
                className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 overflow-x-auto">
              <table className="w-full text-left text-xs border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-[#0f2e46] text-white">
                  <tr>
                    <th className="px-3.5 py-2.5 font-bold border-r border-blue-900/40">Field / Filter</th>
                    <th className="px-3.5 py-2.5 font-bold border-r border-blue-900/40">Admin Options</th>
                    <th className="px-3.5 py-2.5 font-bold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {SPEC_CRITERIA.map((sc, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
                      <td className="px-3.5 py-2.5 font-bold text-gray-900 border-r border-gray-200">
                        {sc.field}
                      </td>
                      <td className="px-3.5 py-2.5 text-gray-700 border-r border-gray-200 font-mono text-[11px]">
                        {sc.options}
                      </td>
                      <td className="px-3.5 py-2.5 text-gray-600">
                        {sc.purpose}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowSpecModal(false)}
                className="px-4 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white text-xs font-semibold rounded-lg shadow-sm"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 2: MANAGE STOCK (ADD / TRANSFER / ADJUST / RELOCATE) ── */}
      <ManageStockModal
        isOpen={isManageModalOpen}
        onClose={() => {
          setIsManageModalOpen(false);
          setActiveProduct(null);
        }}
        product={activeProduct}
        productList={products}
        warehouses={filterOptions.warehouses}
        initialTab={manageModalTab}
        onStockUpdated={() => fetchProducts()}
      />

      {/* ── MODAL 3: STOCK AUDIT LEDGER ── */}
      <StockLedgerModal
        isOpen={isLedgerModalOpen}
        onClose={() => {
          setIsLedgerModalOpen(false);
          setActiveProduct(null);
        }}
        product={activeProduct}
      />
    </section>
  );
};

export default AllInventory;
